import {
  BehoerdendatenDTO,
  BelegDTO,
  GeschaeftskundendatenDTO,
  KundendatenDTO,
  PrivatkundendatenDTO
} from '../openapi/fakturierung-openapi';
import {Belegart} from './belegart.enum';


export function isGeschaeftskundendaten(obj: KundendatenDTO): obj is GeschaeftskundendatenDTO {
  return (obj as GeschaeftskundendatenDTO).typ == 'Geschaeftskundendaten';
}

export function isPrivatkundendaten(obj: KundendatenDTO): obj is PrivatkundendatenDTO {
  return (obj as PrivatkundendatenDTO).typ == 'Privatkundendaten';
}

export function isBehoerdendaten(obj: KundendatenDTO): obj is BehoerdendatenDTO {
  return (obj as BehoerdendatenDTO).typ == 'Behoerdendaten';
}

export function isRechnung(belegart?: string | Belegart): belegart is string {
  if (!belegart) return false;
  return belegart?.toLowerCase() === Belegart.Rechnung.toLowerCase();
}

export function isGutschrift(belegart?: string | Belegart): belegart is string {
  if (!belegart) return false;
  return belegart?.toLowerCase() === Belegart.Gutschrift.toLowerCase();
}

export function isLieferschein(belegart?: string | Belegart): belegart is string {
  if (!belegart) return false;
  return belegart?.toLowerCase() === Belegart.Lieferschein.toLowerCase();
}

export function isBelegart(value?: string): value is Belegart {
  if (!value) return false;
  return Object.values(Belegart).some(belegart => belegart.toLowerCase() === value.toLowerCase());
}

/**
 * Ein Lieferschein kann an eine Rechnung uebergeben werden, sobald er
 * festgeschrieben und noch keiner Rechnung zugeordnet ist.
 */
export function canCreateRechnung(beleg?: BelegDTO): boolean {
  return !!beleg
    && isLieferschein(beleg.belegart)
    && !!beleg.status.festgeschrieben
    && !beleg.status.zuRechnungUebergeben;
}

/**
 * Das Stornieren erzeugt nur bei Rechnung und Gutschrift einen Korrekturbeleg;
 * beim Lieferschein ist es ein reiner Statuswechsel.
 */
export function stornoErzeugtKorrekturbeleg(beleg?: BelegDTO): boolean {
  return !!beleg
    && (isRechnung(beleg.belegart) || isGutschrift(beleg.belegart));
}

/**
 * Ein Beleg kann storniert werden, solange er festgeschrieben und nicht bereits
 * storniert ist. Ein an eine aktive Rechnung uebergebener Lieferschein ist
 * ausgenommen.
 */
export function canStorno(beleg?: BelegDTO): boolean {
  return !!beleg
    && !!beleg.status.festgeschrieben
    && !beleg.status.storniert
    && !beleg.status.zuRechnungUebergeben;
}

/** Die B2G-Referenzfelder gibt es nur bei der Rechnung an einen Geschäfts- oder Behördenkontakt. */
export function hatReferenzfelder(belegart?: string | Belegart, kundendaten?: KundendatenDTO): boolean {
  return isRechnung(belegart)
    && !!kundendaten
    && (isGeschaeftskundendaten(kundendaten) || isBehoerdendaten(kundendaten));
}

/**
 * Bei der Rechnung an eine Behörde ist die Leitweg-ID Pflicht (XRechnung, BR-DE-15).
 *
 * @remarks Muss zur Formatregel des Backends passen (`BelegformatResolver`: RECHNUNG an eine Behördenadresse ergibt XRECHNUNG).
 */
export function leitwegIdPflicht(belegart?: string | Belegart, kundendaten?: KundendatenDTO): boolean {
  return isRechnung(belegart) && !!kundendaten && isBehoerdendaten(kundendaten);
}

/** Rechnungen an eine Behörde erzeugt das Backend als XRechnung (XML) statt als PDF. */
export function isXRechnung(beleg?: Pick<BelegDTO, 'belegformat'>): boolean {
  return beleg?.belegformat === 'XRECHNUNG';
}

/**
 * Die Belegdatei einer XRechnung ist erst nach dem Fertigstellen XML;
 * der Entwurf wird weiterhin als PDF-Vorschau angezeigt.
 */
export function isXRechnungDatei(beleg?: Pick<BelegDTO, 'belegformat'> & { status?: Pick<BelegDTO['status'], 'offen'> }): boolean {
  return isXRechnung(beleg) && !beleg?.status?.offen;
}
